/**
 * The development-only live-harness hook (`useVoiceModeTestDriver`) exposes the
 * current chat input on `window`. The harness can then exercise real session,
 * send, transcription, and playback paths without speaking into a microphone.
 */
import {
  isChatStatusBusy,
  type IDraftStore,
  type RecordingStatus,
} from '@littlebird/sdk';
import { useEffect } from 'react';

import { littlebirdSdk } from '@/lib/sdk-init';

import { submitVoiceModeTestTranscript } from './voice-mode-desktop-platform';

/** Current app state observed by one live-harness scenario */
type VoiceModeTestDriverState = {
  draftContent: string;
  isBusy: boolean;
  ownerIsBusy: boolean;
  activeVoiceModeDraftId: string | null;
  isVoiceModeActive: boolean;
  recordingStatus: RecordingStatus;
  hasPendingSpokenReplies: boolean;
};

/** Development window API exercised by the Voice Mode live harness */
type VoiceModeTestDriver = {
  threadId: string;
  getState: () => VoiceModeTestDriverState;
  sendText: (text: string) => Promise<void>;
  submitTranscript: (transcript: string) => void;
  deactivateVoiceMode: () => void;
  probeSpeechToText: (wavBase64: string) => Promise<string>;
};

/** Mounted composer's state and send operation exposed to the harness */
type VoiceModeTestDriverParams = {
  threadId: string;
  draftId: string;
  draftsStore: IDraftStore;
  sendMessage: () => Promise<void>;
};

declare global {
  interface Window {
    __littlebirdVoiceModeTestDriver?: VoiceModeTestDriver;
  }
}

/** Publish the mounted composer as the development-only live-harness driver */
export function useVoiceModeTestDriver(
  params: VoiceModeTestDriverParams
): void {
  const { threadId, draftId, draftsStore, sendMessage } = params;

  useEffect(() => {
    if (!import.meta.env.DEV) return;

    const voiceModeTestDriver: VoiceModeTestDriver = {
      threadId,
      getState: () => {
        const ownerDraftId = littlebirdSdk.voiceMode.session?.draftId ?? null;
        return {
          draftContent: draftsStore.getDraft(draftId)?.content ?? '',
          isBusy: isChatStatusBusy(
            littlebirdSdk.stores.threads.get(threadId)?.status
          ),
          ownerIsBusy:
            ownerDraftId !== null &&
            isChatStatusBusy(
              littlebirdSdk.stores.threads.get(ownerDraftId)?.status
            ),
          recordingStatus: littlebirdSdk.voiceMode.recordingStatus,
          activeVoiceModeDraftId: ownerDraftId,
          isVoiceModeActive: littlebirdSdk.voiceMode.isActiveForDraft(draftId),
          hasPendingSpokenReplies:
            ownerDraftId !== null &&
            littlebirdSdk.voiceMode.hasPendingSpokenReplies(ownerDraftId),
        };
      },
      sendText: async (text: string) => {
        await draftsStore.updateDraft({ draftId, content: text });
        await sendMessage();
      },
      submitTranscript: submitVoiceModeTestTranscript,
      deactivateVoiceMode: () => littlebirdSdk.voiceMode.reset(),
      probeSpeechToText: (wavBase64) =>
        littlebirdSdk.transcribeSpeech(
          new Blob(
            [Uint8Array.from(atob(wavBase64), (byte) => byte.charCodeAt(0))],
            { type: 'audio/wav' }
          )
        ),
    };
    window.__littlebirdVoiceModeTestDriver = voiceModeTestDriver;

    return () => {
      // Route transitions can mount the next chat input before the previous
      // effect cleans up; the old cleanup must not remove the newer driver
      if (window.__littlebirdVoiceModeTestDriver === voiceModeTestDriver) {
        delete window.__littlebirdVoiceModeTestDriver;
      }
    };
  }, [draftId, draftsStore, sendMessage, threadId]);
}
